import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Camera, Upload, Loader2, AlertTriangle, CheckCircle2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const SEVERITY_STYLE = {
  "Faible": { color: "#00C3A7", bg: "bg-[#00C3A7]/15", label: "text-[#00C3A7]" },
  "Moyen": { color: "#F59E0B", bg: "bg-[#F59E0B]/15", label: "text-[#F59E0B]" },
  "Élevé": { color: "#EF4444", bg: "bg-[#EF4444]/15", label: "text-[#EF4444]" },
};

export const PhotoDiagnostic = () => {
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onFile = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formats acceptés : JPEG, PNG, WEBP");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setB64(reader.result);
      setResult(null);
    };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!b64) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/diagnostic", { image_base64: b64 });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const sev = result ? (SEVERITY_STYLE[result.severity] || SEVERITY_STYLE["Moyen"]) : null;

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="photo-diagnostic-tool">
      <div>
        <div
          data-testid="photo-diagnostic-upload-zone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
          className="relative aspect-[4/3] rounded-2xl border-2 border-dashed border-white/20 bg-white/5 hover:border-[#0D6EFD] cursor-pointer transition-colors overflow-hidden flex items-center justify-center"
        >
          {preview ? (
            <img src={preview} alt="aperçu" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center px-6">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#0D6EFD]/20 flex items-center justify-center mb-3">
                <Camera className="w-7 h-7 text-[#00C3A7]" />
              </div>
              <p className="text-white font-medium">Déposez une photo de votre défaut</p>
              <p className="text-white/50 text-sm mt-1">Fissure, infiltration, malfaçon, tableau électrique…</p>
            </div>
          )}
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
            className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="photo-diagnostic-file-input" />
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => inputRef.current?.click()}
            className="flex-1 border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-full">
            <Upload className="w-4 h-4 mr-2" /> Choisir une photo
          </Button>
          <Button onClick={analyze} disabled={!b64 || loading} data-testid="photo-diagnostic-run-btn"
            className="flex-1 bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse…</> : "Analyser avec l'IA"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-[#121A29] border border-white/10 p-5 min-h-[300px]" data-testid="photo-diagnostic-result-card">
        {!result && !loading && (
          <div className="h-full flex items-center justify-center text-center text-white/40 text-sm">
            Le rapport de diagnostic IA s'affichera ici.
          </div>
        )}
        {loading && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-white/60" data-testid="photo-diagnostic-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#0D6EFD]" />
            <p className="text-sm">Analyse de la pathologie du bâtiment…</p>
          </div>
        )}
        {result && (
          <div className="space-y-4 idb-fade-up">
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${sev.bg} ${sev.label}`}>
                <AlertTriangle className="w-3.5 h-3.5" /> Gravité : {result.severity}
              </span>
            </div>
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Défaut détecté</p>
              <p className="text-white font-semibold">{result.defect_detected}</p>
            </div>
            {result.possible_causes?.length > 0 && (
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Causes probables</p>
                <ul className="space-y-1">
                  {result.possible_causes.map((c, i) => (
                    <li key={`cause-${i}`} className="text-white/80 text-sm flex gap-2"><span className="text-[#F59E0B]">•</span>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.recommended_actions?.length > 0 && (
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Préconisations</p>
                <ul className="space-y-1">
                  {result.recommended_actions.map((a, i) => (
                    <li key={`action-${i}`} className="text-white/80 text-sm flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#00C3A7] shrink-0 mt-0.5" />{a}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-2 pt-2 border-t border-white/10">
              <Wrench className="w-4 h-4 text-[#0D6EFD]" />
              <span className="text-white/60 text-sm">Coût estimé : </span>
              <span className="text-white font-semibold text-sm">{result.estimated_repair_cost}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
